/**
 * Formulaire de changement d'email
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire pour changer l'adresse email avec vérification.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { useAuth } from '../../hooks';
import { ButtonSpinner } from '../ui/LoadingSpinner';
import { CodeInput } from '../ui/CodeInput';

/**
 * Props du composant ChangeEmailForm
 */
export interface ChangeEmailFormProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Email actuel */
  currentEmail: string;
  /** Callback après changement réussi */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Afficher le bouton annuler */
  showCancel?: boolean;
  /** Callback du bouton annuler */
  onCancel?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant ChangeEmailForm
 *
 * @example
 * ```tsx
 * function EmailSettings() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <ChangeEmailForm
 *       userId={user.id}
 *       currentEmail={user.email}
 *       onSuccess={() => toast.success('Email modifié')}
 *     />
 *   );
 * }
 * ```
 */
export function ChangeEmailForm({
  userId,
  currentEmail,
  onSuccess,
  onError,
  showCancel = false,
  onCancel,
  className = '',
}: ChangeEmailFormProps) {
  const { changeEmail, verifyEmailChange, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    nouvelEmail: '',
    motDePasse: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  /**
   * Valider le formulaire
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validation nouvel email
    if (!formData.nouvelEmail) {
      errors.nouvelEmail = 'Le nouvel email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.nouvelEmail)) {
      errors.nouvelEmail = 'Format d\'email invalide';
    } else if (formData.nouvelEmail === currentEmail) {
      errors.nouvelEmail = 'Le nouvel email doit être différent de l\'actuel';
    }

    // Validation mot de passe
    if (!formData.motDePasse) {
      errors.motDePasse = 'Le mot de passe est requis pour confirmer le changement';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Gérer la soumission du formulaire
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      await changeEmail({
        idUtilisateur: userId,
        nouvelEmail: formData.nouvelEmail,
        motDePasse: formData.motDePasse,
      });

      // Passer en mode vérification de code
      setPendingEmail(formData.nouvelEmail);
      setShowVerificationCode(true);

      // Réinitialiser le mot de passe pour sécurité
      setFormData({
        nouvelEmail: formData.nouvelEmail,
        motDePasse: '',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de changement d\'email';
      onError?.(message);
    }
  };

  /**
   * Gérer la vérification du code
   */
  const handleVerifyCode = async (code: string) => {
    clearError();

    try {
      await verifyEmailChange({
        idUtilisateur: userId,
        nouvelEmail: pendingEmail,
        code,
      });

      // Réinitialiser le formulaire
      setFormData({
        nouvelEmail: '',
        motDePasse: '',
      });
      setShowVerificationCode(false);
      setPendingEmail('');

      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Code de vérification invalide';
      onError?.(message);
    }
  };

  /**
   * Gérer le changement de champ
   */
  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Effacer l'erreur du champ modifié
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Si on doit vérifier le code, afficher le formulaire de vérification
  if (showVerificationCode) {
    return (
      <div className={`w-full max-w-md ${className}`}>
        <div className="space-y-6">
          {/* Titre */}
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-accent-light rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-content">
              Vérifiez votre email
            </h3>
            <p className="mt-2 text-sm text-content-muted">
              Un code de vérification a été envoyé à{' '}
              <span className="font-medium">{pendingEmail}</span>
            </p>
          </div>

          {/* Erreur globale */}
          {error && (
            <div
              className="p-4 bg-danger-light border border-danger rounded-lg"
              role="alert"
            >
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Composant de saisie du code */}
          <CodeInput
            onComplete={handleVerifyCode}
            disabled={isLoading}
            error={error || undefined}
            helpText="Entrez le code à 6 chiffres reçu par email"
            autoSubmit={true}
            allowPaste={true}
          />

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-center justify-center">
              <ButtonSpinner className="mr-2" />
              <span className="text-sm text-content-muted">
                Vérification en cours...
              </span>
            </div>
          )}

          {/* Bouton retour */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setShowVerificationCode(false);
                setPendingEmail('');
                clearError();
              }}
              disabled={isLoading}
              className="text-sm font-medium text-primary-text hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Retour au formulaire
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de changement d'email
  return (
    <div className={`w-full max-w-md ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Titre */}
        <div>
          <h3 className="text-lg font-semibold text-content">
            Changer l'adresse email
          </h3>
          <p className="mt-1 text-sm text-content-muted">
            Email actuel : <span className="font-medium">{currentEmail}</span>
          </p>
        </div>

        {/* Erreur globale */}
        {error && (
          <div
            className="p-4 bg-danger-light border border-danger rounded-lg"
            role="alert"
          >
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {/* Info */}
        <div className="p-4 bg-accent-light border border-accent rounded-lg">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-accent mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-accent">
              Après le changement, vous devrez vérifier votre nouvelle adresse email avant de pouvoir
              l'utiliser pour vous connecter.
            </p>
          </div>
        </div>

        {/* Nouvel email */}
        <div>
          <label
            htmlFor="nouvel-email"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Nouvelle adresse email
          </label>
          <input
            id="nouvel-email"
            type="email"
            value={formData.nouvelEmail}
            onChange={handleChange('nouvelEmail')}
            disabled={isLoading}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
              formErrors.nouvelEmail
                ? 'border-danger'
                : 'border-border'
            } bg-surface text-content`}
            placeholder="nouvel@email.com"
            autoComplete="email"
          />
          {formErrors.nouvelEmail && (
            <p className="mt-1 text-sm text-danger">
              {formErrors.nouvelEmail}
            </p>
          )}
        </div>

        {/* Mot de passe de confirmation */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Mot de passe actuel
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.motDePasse}
              onChange={handleChange('motDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.motDePasse
                  ? 'border-danger'
                  : 'border-border'
              } bg-surface text-content`}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
              tabIndex={-1}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {formErrors.motDePasse && (
            <p className="mt-1 text-sm text-danger">
              {formErrors.motDePasse}
            </p>
          )}
          <p className="mt-1 text-xs text-content-subtle">
            Entrez votre mot de passe actuel pour confirmer le changement
          </p>
        </div>

        {/* Boutons */}
        <div className="flex gap-3">
          {showCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-content-muted bg-surface hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <ButtonSpinner className="mr-2" />
                Modification...
              </>
            ) : (
              'Changer l\'email'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
